import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useAuth } from "../../App";
import { usePermissions } from "../../context/PermissionsContext";
import CRMContextBanner from "../../components/CRMContextBanner";
import CRMSidebar from "../../components/CRMSidebar";
import API from "../../utils/api";
import PipelineFunnel from "../../components/PipelineFunnel";
import AnalyticsDashboard from "../../components/AnalyticsDashboard";
import ReportsExport from "../../components/ReportsExport";
import SalesLearning from "../../components/SalesLearning";
import FieldDayMap from "../../components/FieldDayMap";
import TargetCenter from "../../components/TargetCenter";
import AutomationRules from "../admin/AutomationRules";
import TopDeals from "../../components/TopDeals";
import BulkUpload from "../../components/BulkUpload";
import DailyProductivity from "../DailyProductivity";
import LeadActionCenter from "../../components/LeadActionCenter";
import FollowUpSlaDashboard from "../../components/FollowUpSlaDashboard";
import LeadDataQuality from "../../components/LeadDataQuality";
import InstitutionAccounts from "../../components/InstitutionAccounts";
import ExistingCustomerImport from "../../components/ExistingCustomerImport";
import RevenueForecast from "../../components/RevenueForecast";
import FieldGrowthCenter from "../../components/FieldGrowthCenter";
import FieldBriefing from "../../components/FieldBriefing";
import CommandCenter from "../../components/CommandCenter";

const HRMS_URL      = process.env.REACT_APP_HRMS_URL      || "http://localhost:5000";
const EXPENSE_URL     = process.env.REACT_APP_EXPENSE_URL     || "http://localhost:4000";
const EXPENSE_API_URL = process.env.REACT_APP_EXPENSE_API_URL || "http://localhost:4000/api";

const PLAN_ICONS = { institution_visit:"FV", office:"WFO", wfh:"WFH", exam_duty:"EX", planned_leave:"LV" };
const STATUS_CFG = { hot:["#fee2e2","#dc2626"], warm:["#fef9c3","#b45309"], cold:["#dbeafe","#1d4ed8"] };

// Maps CRM manager nav keys → permission module keys (null = always show)
// Keys MUST match what AccessControl.jsx stores in role_permissions.module_key
const MANAGER_NAV_MODULE_MAP = {
  home:        null,
  team:        null,
  leads:       null,               // core My Team view
  followups:   null,               // core My Team view
  field:       null,               // core My Team visits
  briefing:    null,               // the evening management page — manager-inherent
  performance: null,               // no DB key — manager-inherent, always show
  pipeline:    null,               // no DB key — manager-inherent, always show
  flagged:     null,               // no DB key — manager-inherent, always show
  analytics:   null,               // no DB key — manager-inherent, always show
  targets:     null,               // no DB key — manager-inherent, always show
  "top-deals": null,               // no DB key — manager-inherent, always show
  reports:     "reports",
  learning:    "learning",
  // Admin-delegatable tabs — visible only when granted via AccessControl Individual Access
  automation:  "automation",
  upload:       "bulk_message",
  productivity: null,
  actions:      null,
  quality:      null,
  accounts:     null,
};

const ALL_MANAGER_NAV = [
  ["home",         "⌂", "Home"],
  ["team",         "👥", "My Team"],
  ["followups",    "📞", "Follow-ups"],
  ["leads",        "🎯", "Leads"],
  ["field",        "🗺️", "Field Day"],
  ["briefing",     "📊", "Field Briefing"],
  ["productivity", "📈", "Team Productivity"],
  ["actions",      "✅", "Lead Action Requests"],
  ["targets",      "🏆", "Targets"],
  ["performance",  "📊", "Performance"],
  ["pipeline",     "🔄", "Pipeline"],
  ["flagged",      "🚩", "Flagged"],
  ["top-deals",    "💎", "Top Deals"],
  ["accounts",     "AC", "Institutions"],
  ["customers",    "CU", "Existing Clients"],
  ["quality",      "DQ", "Data Quality"],
  ["growth",       "FG", "Field Growth"],
  ["forecast",     "FC", "Forecast"],
  ["analytics",    "📈", "Analytics"],
  ["reports",      "📋", "Reports"],
  ["learning",     "🎓", "Learning"],
  // Admin-delegatable — only rendered when can() returns true via individual access grant
  ["automation",   "⚡", "Automation"],
  ["upload",       "📥", "Bulk Upload"],
];

export default function ManagerDashboard() {
  const { user, logout } = useAuth();
  const { can } = usePermissions();
  const navigate = useNavigate();
  const [tab, setTab]             = useState("home");
  const [todayData, setTodayData] = useState({ checkins:[], employees:[] });
  const [performance, setPerf]    = useState([]);
  const [leads, setLeads]         = useState([]);
  const [leadSearch, setLeadSearch] = useState("");
  const [leadFocus, setLeadFocus] = useState("all");
  const [leadAssignees, setLeadAssignees] = useState([]);
  const [assigningLeadId, setAssigningLeadId] = useState(null);
  const [followupStats, setFollowupStats] = useState([]);
  const [teamFollowups, setTeamFollowups] = useState([]);
  const [flaggedActions, setFlaggedActions] = useState([]);
  const [loading, setLoading]     = useState(true);
  // Field Day tracking
  const [fieldDate, setFieldDate] = useState(new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }));
  const [fieldCheckins, setFieldCheckins] = useState([]);
  const [fieldLoading, setFieldLoading]   = useState(false);
  const [fieldMapUser, setFieldMapUser]   = useState(null); // { id, name }

  const accent = user?.company === "ebekmed" ? "#2AB5A5" : "#E31E24";

  // Filter manager nav based on module permissions (derived after all hooks)
  const MANAGER_NAV = ALL_MANAGER_NAV.filter(([k]) => {
    const modKey = MANAGER_NAV_MODULE_MAP[k];
    if (!modKey) return true;
    return can(modKey);
  }).map(([k, icon, label]) => [k, icon, k === "field" ? "Visits" : label]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [todayRes, perfRes, leadsRes, fuStatsRes, followupsRes, flaggedRes, usersRes] = await Promise.all([
        API.get("/checkins/team/today").catch(() => ({ data: { checkins: [], employees: [] } })),
        API.get("/reports/performance").catch(() => ({ data: [] })),
        API.get("/leads/team").catch(() => ({ data: [] })),
        API.get("/followups/stats").catch(() => ({ data: [] })),
        API.get("/followups/team").catch(() => ({ data: [] })),
        API.get("/follow-up-actions/flagged").catch(() => ({ data: [] })),
        API.get("/users").catch(() => ({ data: [] })),
      ]);
      setTodayData(todayRes.data || { checkins: [], employees: [] });
      setPerf(perfRes.data || []);
      setLeads(leadsRes.data || []);
      setFollowupStats(fuStatsRes.data || []);
      setTeamFollowups(followupsRes.data || []);
      setFlaggedActions(flaggedRes.data || []);
      setLeadAssignees((usersRes.data || []).filter(member => member.is_active && !["superadmin", "executive"].includes(member.role)));
    } catch { toast.error("Failed to load dashboard"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const checkedIn  = todayData.checkins;

  const leadToday = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const visibleTeamLeads = leads.filter(lead => {
    const query = leadSearch.trim().toLowerCase();
    if (query && ![lead.institution_name, lead.contact_name, lead.rep_name]
      .some(value => String(value || "").toLowerCase().includes(query))) return false;
    if (leadFocus === "hot" && lead.lead_status !== "hot") return false;
    if (leadFocus === "overdue" && !(lead.next_follow_up && String(lead.next_follow_up).slice(0, 10) < leadToday)) return false;
    if (leadFocus === "no_followup" && lead.next_follow_up) return false;
    if (leadFocus === "high_value" && Math.max(Number(lead.deal_value) || 0, Number(lead.approx_revenue) || 0) < 100000) return false;
    return true;
  }).sort((a, b) => (Number(b.deal_score) || 0) - (Number(a.deal_score) || 0));

  const assignLead = async (lead, userId) => {
    const target = leadAssignees.find(member => Number(member.id) === Number(userId));
    if (!target || Number(lead.user_id) === Number(userId)) return;
    if (!window.confirm(`Assign ${lead.institution_name} to ${target.name}? Open follow-ups will also move.`)) return;
    setAssigningLeadId(lead.id);
    try {
      const response = await API.put(`/leads/${lead.id}/assign`, { user_id: Number(userId) });
      setLeads(current => current.map(item => item.id === lead.id ? { ...item, ...response.data } : item));
      toast.success(`Lead assigned to ${target.name}`);
    } catch (error) {
      toast.error(error?.response?.data?.error || "Could not assign lead");
    } finally {
      setAssigningLeadId(null);
    }
  };

  const openTab = (k) => {
    setTab(k);
    if (k === "field") {
      setFieldLoading(true);
      const d = fieldDate;
      API.get(`/visits/team?from=${d}&to=${d}`).then(r => {
        setFieldCheckins((r.data || []).map(v => ({ ...v, name: v.rep_name })));
      }).catch(() => {}).finally(() => setFieldLoading(false));
    }
  };

  return (
    <div className="sales-admin-shell sales-manager-shell" style={{ minHeight:"100vh", display:"flex", fontFamily:"'Inter',sans-serif" }}>
      {/* Vertical Sidebar */}
      <CRMSidebar
        user={user}
        tab={tab}
        setTab={openTab}
        navItems={MANAGER_NAV}
        flaggedCount={flaggedActions.length}
        logout={logout}
      />

      {/* Main content */}
      <div style={{ flex:1, minWidth:0, overflowY:"auto", background:"#f8fafc" }}>
        <div style={{ background:"#fff", borderBottom:"1px solid #e2e8f0", padding:"0 20px", display:"flex", alignItems:"center", height:56, position:"sticky", top:0, zIndex:30 }}>
          <div style={{ minWidth:0, flex:1 }}>
            <div style={{ fontSize:14, fontWeight:800, color:"#1e293b" }}>
              {MANAGER_NAV.find(n => n[0] === tab)?.[2] || "Home"}
            </div>
            <div style={{ fontSize:10, color:"#94a3b8" }}>{user?.name} · Manager View</div>
          </div>
          <button className="sales-quick-add team-quick-visit" onClick={() => {
            sessionStorage.setItem("crm_quick_visit", "1");
            navigate("/crm-self");
          }}>+ Quick Visit</button>
        </div>

      <div className="sales-admin-content" style={{ padding:"20px", maxWidth: tab === "home" ? 1100 : 800, margin:"0 auto" }}>
        {loading && tab !== "home" && <div style={{ textAlign:"center", padding:60, color:"#94a3b8" }}>Loading...</div>}

        {tab === "home" && (
          <CommandCenter
            accent={accent}
            onOpenTab={openTab}
            onOpenPerson={(person, day) => setFieldMapUser({ id: person.user_id, name: person.name, date: day })}
          />
        )}


        {/* FIELD DAY TAB */}
        {tab === "field" && (
          <>
            {/* Date picker + refresh */}
            <div style={{ display:"flex", alignItems:"center", gap:12, marginBottom:16, background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"12px 16px" }}>
              <span style={{ fontSize:13, fontWeight:700, color:"#475569" }}>Date:</span>
              <input
                type="date"
                value={fieldDate}
                onChange={e => {
                  const d = e.target.value;
                  setFieldDate(d);
                  setFieldLoading(true);
                  API.get(`/visits/team?from=${d}&to=${d}`)
                    .then(r => setFieldCheckins((r.data || []).map(v => ({ ...v, name: v.rep_name }))))
                    .catch(() => {})
                    .finally(() => setFieldLoading(false));
                }}
                style={{ border:"1px solid #e2e8f0", borderRadius:8, padding:"6px 10px", fontSize:13, fontFamily:"inherit" }}
              />
              <span style={{ fontSize:12, color:"#94a3b8", marginLeft:4 }}>
                {fieldCheckins.length} rep{fieldCheckins.length !== 1 ? "s" : ""} on field visit
              </span>
            </div>

            {fieldLoading && <div style={{ textAlign:"center", padding:40, color:"#94a3b8" }}>Loading...</div>}

            {!fieldLoading && fieldCheckins.length === 0 && (
              <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:60, textAlign:"center" }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📍</div>
                <div style={{ fontWeight:700, color:"#1e293b", marginBottom:4 }}>No field visits on this day</div>
                <div style={{ fontSize:13, color:"#94a3b8" }}>Reps checked in as "Institution Visit" will appear here</div>
              </div>
            )}

            {!fieldLoading && (() => {
              const people = [];
              const byId = new Map();
              for (const v of fieldCheckins) {
                const id = v.user_id;
                if (!byId.has(id)) {
                  const row = { user_id: id, name: v.name || v.rep_name, visits: 0 };
                  byId.set(id, row);
                  people.push(row);
                }
                byId.get(id).visits += 1;
              }
              return people.map(c => (
              <div
                key={c.user_id}
                style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"14px 16px", marginBottom:10, display:"flex", alignItems:"center", gap:14, cursor:"pointer", transition:"box-shadow 0.15s" }}
                onMouseEnter={e => e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.08)"}
                onMouseLeave={e => e.currentTarget.style.boxShadow = "none"}
                onClick={() => setFieldMapUser({ id: c.user_id, name: c.name, date: fieldDate })}
              >
                <div style={{ width:44, height:44, borderRadius:12, background: accent + "20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:18, fontWeight:900, color:accent, flexShrink:0 }}>
                  {(c.name || "?")[0]}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:14, fontWeight:700, color:"#1e293b" }}>{c.name}</div>
                  <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}>
                    {c.visits} institution{c.visits === 1 ? "" : "s"} — photos, GPS and start → finish map
                  </div>
                </div>
                <div style={{ background: accent + "15", color: accent, borderRadius:20, padding:"6px 14px", fontSize:12, fontWeight:700, flexShrink:0 }}>
                  Photos & map →
                </div>
              </div>
              ));
            })()}

          </>
        )}

        {/* TEAM TAB */}
        {!loading && tab === "team" && (
          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, overflow:"hidden" }}>
            <div style={{ padding:"16px 20px", borderBottom:"1px solid #f1f5f9", fontSize:14, fontWeight:700 }}> Team Members</div>
            {todayData.employees.map(e => {
              const c = checkedIn.find(x => x.user_id === e.id);
              return (
                <div key={e.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 20px", borderBottom:"1px solid #f8fafc" }}>
                  <div style={{ width:40, height:40, borderRadius:12, background:accent+"20", display:"flex", alignItems:"center", justifyContent:"center", fontSize:16, fontWeight:800, color:accent, flexShrink:0 }}>
                    {e.name[0]}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:14, fontWeight:700, color:"#1e293b" }}>{e.name}</div>
                    <div style={{ fontSize:12, color:"#94a3b8" }}>{e.phone || e.email}</div>
                  </div>
                  {c ? (
                    <div style={{ textAlign:"right" }}>
                      <div style={{ fontSize:16 }}>{PLAN_ICONS[c.plan_type]}</div>
                      <div style={{ fontSize:11, color:"#16a34a", fontWeight:600 }}>Active</div>
                    </div>
                  ) : (
                    <div style={{ background:"#fee2e2", borderRadius:8, padding:"4px 10px", fontSize:11, fontWeight:700, color:"#dc2626" }}>Not In</div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* LEADS TAB */}
        {!loading && tab === "leads" && (
          <>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:12, marginBottom:16 }}>
              {["hot","warm","cold"].map(s => {
                const [bg,fg] = STATUS_CFG[s];
                const count = leads.filter(l => l.lead_status===s).length;
                return (
                  <div key={s} style={{ background:"#fff", border:`1.5px solid ${fg}30`, borderRadius:16, padding:"16px 12px", textAlign:"center" }}>
                    <div style={{ fontSize:28, fontWeight:900, color:fg }}>{count}</div>
                    <div style={{ fontSize:12, color:fg, fontWeight:700 }}>{s.toUpperCase()}</div>
                  </div>
                );
              })}
            </div>
            <input
              type="search"
              value={leadSearch}
              onChange={event => setLeadSearch(event.target.value)}
              placeholder="Search institution, contact or employee"
              aria-label="Search team leads"
              style={{ width:"100%", boxSizing:"border-box", padding:"11px 12px", border:"1.5px solid #e2e8f0", borderRadius:10, fontFamily:"inherit", fontSize:12, marginBottom:8 }}
            />
            <div style={{ display:"flex", gap:6, overflowX:"auto", paddingBottom:5, marginBottom:10 }}>
              {[["all","All"],["hot","Hot"],["overdue","Overdue"],["no_followup","No follow-up"],["high_value","High value"]].map(([key,label]) => (
                <button key={key} onClick={() => setLeadFocus(key)} style={{ padding:"7px 10px", borderRadius:8, border:`1px solid ${leadFocus===key?accent:"#e2e8f0"}`, background:leadFocus===key?accent+"14":"#fff", color:leadFocus===key?accent:"#64748b", fontFamily:"inherit", fontSize:10, fontWeight:800, whiteSpace:"nowrap", flexShrink:0, cursor:"pointer" }}>{label}</button>
              ))}
            </div>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {visibleTeamLeads.length === 0 && <div style={{ padding:30, textAlign:"center", color:"#94a3b8", background:"#fff", borderRadius:14 }}>No leads match this view</div>}
              {visibleTeamLeads.slice(0,50).map(l => (
                <div key={l.id} style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:14, padding:"14px 16px" }}>
                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                    <div style={{ fontSize:14, fontWeight:700 }}>{l.institution_name}</div>
                    <span style={{ background:l.lead_status==="hot"?"#fee2e2":l.lead_status==="warm"?"#fef3c7":"#e0f2fe", color:l.lead_status==="hot"?"#dc2626":l.lead_status==="warm"?"#b45309":"#0369a1", borderRadius:100, padding:"3px 10px", fontSize:11, fontWeight:700 }}>{l.lead_status || "warm"}</span>
                  </div>
                  <div style={{ fontSize:12, color:"#64748b" }}>Rep: {l.rep_name} · {(l.pipeline_stage || l.lead_stage)?.replaceAll("_"," ")}</div>
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginTop:6 }}>
                    {l.next_follow_up && <span style={{ fontSize:10, padding:"3px 7px", borderRadius:6, background:String(l.next_follow_up).slice(0,10)<leadToday?"#fee2e2":"#fef9c3", color:String(l.next_follow_up).slice(0,10)<leadToday?"#dc2626":"#92400e" }}>Follow-up {new Date(l.next_follow_up).toLocaleDateString("en-IN")}</span>}
                    {!l.next_follow_up && <span style={{ fontSize:10, padding:"3px 7px", borderRadius:6, background:"#fff7ed", color:"#c2410c" }}>No follow-up</span>}
                    {(l.deal_value || l.approx_revenue) && <span style={{ fontSize:10, padding:"3px 7px", borderRadius:6, background:"#dcfce7", color:"#15803d" }}>Rs. {Number(l.deal_value || l.approx_revenue).toLocaleString("en-IN")}</span>}
                  </div>
                  <div style={{ marginTop:8 }}>
                    <select
                      value={l.user_id || ""}
                      disabled={assigningLeadId === l.id}
                      onChange={event => assignLead(l, event.target.value)}
                      aria-label={`Assign ${l.institution_name}`}
                      style={{ width:"100%", padding:"7px 9px", border:"1px solid #e2e8f0", borderRadius:8, background:"#f8fafc", color:"#475569", fontFamily:"inherit", fontSize:10, fontWeight:700 }}
                    >
                      {leadAssignees
                        .filter(member => member.company === l.company && (
                          ["admin", "superadmin"].includes(user?.role)
                          || Number(member.id) === Number(user?.id)
                          || Number(member.manager_id) === Number(user?.id)
                          || leadAssignees.some(manager => Number(manager.id) === Number(member.manager_id) && Number(manager.manager_id) === Number(user?.id))
                        ))
                        .map(member => <option key={member.id} value={member.id}>Owner: {member.name}</option>)}
                    </select>
                  </div>
                  <div style={{ fontSize:11, color:"#64748b", marginTop:3 }}>{l.companions?.length ? `Companion: ${l.companions.map(person => person.name).join(", ")}` : "Solo visit"}</div>
                </div>
              ))}
            </div>
          </>
        )}


        {/* ANALYTICS TAB */}
        {tab === "analytics" && (
          <AnalyticsDashboard accent={accent} userRole={user?.role} userCompany={user?.company} />
        )}

        {/* REPORTS TAB */}
        {tab === "reports" && (
          <ReportsExport accent={accent} userRole={user?.role} userCompany={user?.company} />
        )}

        {/* LEARNING TAB */}
        {tab === "learning" && (
          <SalesLearning accent={accent} />
        )}

        {tab === "targets" && (
          <TargetCenter accent={accent} managementView />
        )}

        {/* TOP DEALS TAB */}
        {tab === "top-deals" && <TopDeals accent={accent} limit={20} />}

        {/* AUTOMATION TAB */}
        {tab === "automation" && <AutomationRules accent={accent} />}

        {/* PIPELINE TAB */}
        {tab === "pipeline" && <PipelineFunnel accent={accent} />}
        {tab === "customers" && <ExistingCustomerImport accent={accent} />}
        {tab === "forecast" && <RevenueForecast accent={accent} />}
        {tab === "growth" && <FieldGrowthCenter accent={accent} />}
        {tab === "briefing" && <FieldBriefing accent={accent} />}

        {tab === "actions" && <LeadActionCenter accent={accent} />}
        {tab === "quality" && <LeadDataQuality accent={accent} />}
        {tab === "accounts" && <InstitutionAccounts accent={accent} />}

        {/* FOLLOW-UPS TAB */}
        {!loading && tab === "followups" && (
          <>
            <FollowUpSlaDashboard accent={accent} />
            {/* Summary totals */}
            <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10, marginBottom:16 }}>
              {[
                { label:"Total",    value: followupStats.reduce((a,r)=>a+parseInt(r.total_followups||0),0),  color:"#6366f1", icon:"" },
                { label:"Done",     value: followupStats.reduce((a,r)=>a+parseInt(r.completed||0),0),         color:"#16a34a", icon:"" },
                { label:"Missed",   value: followupStats.reduce((a,r)=>a+parseInt(r.missed||0),0),            color:"#dc2626", icon:"" },
                { label:"Overdue",  value: followupStats.reduce((a,r)=>a+parseInt(r.overdue||0),0),           color:"#ea580c", icon:"" },
              ].map(s => (
                <div key={s.label} style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, padding:"14px 8px", textAlign:"center" }}>
                  <div style={{ fontSize:18, marginBottom:3 }}>{s.icon}</div>
                  <div style={{ fontSize:24, fontWeight:900, color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:10, color:"#94a3b8", fontWeight:600 }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Per-employee breakdown */}
            <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, overflow:"hidden" }}>
              <div style={{ padding:"16px 20px", borderBottom:"1px solid #f1f5f9", fontSize:14, fontWeight:700 }}> Per Employee (Last 30 Days)</div>
              {followupStats.length === 0 ? (
                <div style={{ padding:30, textAlign:"center", color:"#94a3b8", fontSize:13 }}>No follow-up data yet</div>
              ) : followupStats.map((r, i) => {
                const total = parseInt(r.total_followups || 0);
                const done  = parseInt(r.completed || 0);
                const miss  = parseInt(r.missed    || 0);
                const over  = parseInt(r.overdue   || 0);
                const pct   = total > 0 ? Math.round(done / total * 100) : 0;
                return (
                  <div key={r.user_id} style={{ padding:"14px 20px", borderBottom:"1px solid #f8fafc" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:8 }}>
                      <div style={{ width:34, height:34, borderRadius:10, background:accent+"20", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, color:accent, fontSize:13, flexShrink:0 }}>
                        {r.rep_name?.[0] || "?"}
                      </div>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13, fontWeight:700, color:"#1e293b" }}>{r.rep_name}</div>
                        <div style={{ fontSize:11, color:"#94a3b8" }}>{r.rep_company}</div>
                      </div>
                      <div style={{ textAlign:"right" }}>
                        <div style={{ fontSize:18, fontWeight:900, color:accent }}>{total}</div>
                        <div style={{ fontSize:10, color:"#94a3b8" }}>total</div>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div style={{ background:"#f1f5f9", borderRadius:6, height:6, marginBottom:6, overflow:"hidden" }}>
                      <div style={{ height:"100%", background: pct>=70?"#16a34a": pct>=40?"#f59e0b":"#dc2626", width:`${pct}%`, borderRadius:6, transition:"width 0.5s" }} />
                    </div>
                    {/* Stat pills */}
                    <div style={{ display:"flex", gap:6 }}>
                      <span style={{ padding:"2px 8px", background:"#f0fdf4", color:"#16a34a", borderRadius:20, fontSize:10, fontWeight:700 }}>{done} done</span>
                      {miss > 0 && <span style={{ padding:"2px 8px", background:"#fef2f2", color:"#dc2626", borderRadius:20, fontSize:10, fontWeight:700 }}>{miss} missed</span>}
                      {over > 0 && <span style={{ padding:"2px 8px", background:"#fff7ed", color:"#ea580c", borderRadius:20, fontSize:10, fontWeight:700 }}> {over} overdue</span>}
                      <span style={{ padding:"2px 8px", background:"#f5f3ff", color:"#7c3aed", borderRadius:20, fontSize:10, fontWeight:700 }}>{pct}% rate</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, overflow:"hidden", marginTop:16 }}>
              <div style={{ padding:"16px 20px", borderBottom:"1px solid #f1f5f9", fontSize:14, fontWeight:700 }}>Team Follow-up List</div>
              {teamFollowups.length === 0 ? (
                <div style={{ padding:24, textAlign:"center", color:"#94a3b8", fontSize:13 }}>No team follow-ups in this period</div>
              ) : teamFollowups.map(f => (
                <div key={f.id} style={{ padding:"12px 16px", borderBottom:"1px solid #f1f5f9", minWidth:0 }}>
                  <div style={{ display:"flex", justifyContent:"space-between", gap:10, alignItems:"flex-start" }}>
                    <div style={{ minWidth:0 }}>
                      <div style={{ fontSize:13, fontWeight:800, color:"#1e293b", overflowWrap:"anywhere" }}>{f.institution_name || f.contact_name || "Follow-up"}</div>
                      <div style={{ fontSize:11, color:"#64748b", marginTop:3 }}>{f.rep_name} · {f.scheduled_date ? new Date(f.scheduled_date).toLocaleDateString("en-IN") : "No date"}</div>
                    </div>
                    <span style={{ flexShrink:0, padding:"3px 8px", borderRadius:999, background:"#f1f5f9", color:"#475569", fontSize:10, fontWeight:800 }}>{f.status || "pending"}</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* FLAGGED ACTIONS TAB */}
        {tab === "flagged" && (
          <>
            <div style={{ marginBottom:16 }}>
              <div style={{ fontSize:16, fontWeight:800, color:"#1e293b" }}> Flagged Location Mismatches</div>
              <div style={{ fontSize:12, color:"#94a3b8", marginTop:2 }}>
                Team members who logged a Direct Meeting but were &gt;500m from the institution - visible to you only
              </div>
            </div>

            {flaggedActions.length === 0 ? (
              <div style={{ textAlign:"center", padding:60, background:"#fff", borderRadius:16, border:"1px solid #e2e8f0" }}>
                <div style={{ fontSize:48, marginBottom:12 }}></div>
                <div style={{ fontWeight:700, color:"#1e293b", marginBottom:4 }}>No flagged actions</div>
                <div style={{ fontSize:13, color:"#94a3b8" }}>All direct meets have been within the expected range</div>
              </div>
            ) : flaggedActions.map(a => (
              <div key={a.id} style={{ background:"#fff", border:"1.5px solid #fecaca", borderRadius:14, padding:16, marginBottom:10 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:10 }}>
                  <div>
                    <div style={{ fontSize:14, fontWeight:800, color:"#1e293b" }}>{a.institution_name}</div>
                    <div style={{ fontSize:12, color:"#64748b", marginTop:2 }}> {a.employee_name}</div>
                    <div style={{ fontSize:11, color:"#94a3b8", marginTop:1 }}>
                      {new Date(a.created_at).toLocaleString("en-IN",{day:"numeric",month:"short",year:"numeric",hour:"2-digit",minute:"2-digit"})}
                    </div>
                  </div>
                  <div style={{ padding:"4px 12px", background:"#fef2f2", borderRadius:20, color:"#dc2626", fontWeight:800, fontSize:13, flexShrink:0 }}>
                     {a.location_mismatch_meters}m away
                  </div>
                </div>
                {a.manager_flag_note && (
                  <div style={{ background:"#fef2f2", borderRadius:10, padding:"8px 12px", fontSize:12, color:"#9a3412", lineHeight:1.5 }}>
                     {a.manager_flag_note}
                  </div>
                )}
                {a.notes && (
                  <div style={{ marginTop:8, fontSize:12, color:"#64748b", background:"#f8fafc", borderRadius:8, padding:"6px 10px" }}>
                     {a.notes}
                  </div>
                )}
              </div>
            ))}
          </>
        )}

        {/* PERFORMANCE TAB */}
        {!loading && tab === "performance" && (
          <div style={{ background:"#fff", border:"1px solid #e2e8f0", borderRadius:16, overflow:"hidden" }}>
            <div style={{ padding:"16px 20px", borderBottom:"1px solid #f1f5f9", fontSize:14, fontWeight:700 }}> Last 30 Days</div>
            {performance.map((p, i) => (
              <div key={p.id} style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 20px", borderBottom:"1px solid #f8fafc" }}>
                <div style={{ width:28, height:28, borderRadius:8, background:i===0?accent+"20":"#f1f5f9", display:"flex", alignItems:"center", justifyContent:"center", fontSize:13, fontWeight:800, color:i===0?accent:"#94a3b8" }}>
                  {i+1}
                </div>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#1e293b" }}>{p.name}</div>
                  <div style={{ fontSize:12, color:"#64748b" }}>{p.days_active} days active - {p.hot_leads} hot leads</div>
                </div>
                <div style={{ textAlign:"right" }}>
                  <div style={{ fontSize:20, fontWeight:900, color:accent }}>{p.total_visits}</div>
                  <div style={{ fontSize:10, color:"#94a3b8" }}>visits</div>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Admin-delegatable tabs — shown only when granted via AccessControl Individual Access */}
        {tab === "automation" && <AutomationRules accent={accent} />}
        {tab === "upload"     && <BulkUpload accent={accent} />}
        {tab === "productivity" && <DailyProductivity />}

      </div>  {/* sales-admin-content */}
      {fieldMapUser && (
        <FieldDayMap
          userId={fieldMapUser.id}
          userName={fieldMapUser.name}
          date={fieldMapUser.date || fieldDate}
          accent={accent}
          onClose={() => setFieldMapUser(null)}
        />
      )}
      </div>  {/* main content */}
    </div>
  );
}
